// frontend/packages/business-console/src/services/visitorsService.js
//
// Thin client over /api/v2/visitors/*. Business context is supplied via
// the x-business-id header (axios interceptor in @upryzze/services).
//
// See docs/design/site-analytics.md §9 in the platform repo.

import { api } from '@upryzze/services';

/**
 * GET /api/v2/visitors/summary
 *
 * Powers the Pulse "Visitors" card. Returns:
 *   { today: {...}, yesterday: {...}|null, sparkline: [{date, sessions}] }
 */
export async function getVisitorsSummary() {
  const { data } = await api.get('/v2/visitors/summary');
  return (
    data || {
      today: {
        sessions: 0,
        uniqueVisitors: 0,
        pageViews: 0,
        formStarts: 0,
        formSubmits: 0,
        phoneTaps: 0,
      },
      yesterday: null,
      sparkline: [],
    }
  );
}

export async function getVisitorsTimeseries(days = 14) {
  const { data } = await api.get('/v2/visitors/timeseries', {
    params: { days },
  });
  return data || { days, items: [] };
}

export async function getVisitorsFunnel(days = 14) {
  const { data } = await api.get('/v2/visitors/funnel', { params: { days } });
  return (
    data || {
      days,
      sessions: 0,
      scrolled75: 0,
      formStarts: 0,
      formAttempts: 0,
      formSubmits: 0,
    }
  );
}

export async function getVisitorsTopPages(days = 14) {
  const { data } = await api.get('/v2/visitors/top-pages', {
    params: { days },
  });
  return data || { days, items: [] };
}

export async function getVisitorsSources(days = 14) {
  const { data } = await api.get('/v2/visitors/sources', { params: { days } });
  return data || { days, referrers: [], utm: [] };
}

/**
 * GET /api/v2/visitors/ai
 *
 * The differentiator. AI crawler hits with training vs runtime split +
 * per-bot breakdown. Powers the "Who's the AI saying you exist to?" panel.
 */
export async function getVisitorsAi(days = 14) {
  const { data } = await api.get('/v2/visitors/ai', { params: { days } });
  return (
    data || {
      days,
      totalHits: 0,
      totalTraining: 0,
      totalRuntime: 0,
      byBot: [],
      sparkline: [],
    }
  );
}

/**
 * GET /api/v2/visitors/attribution
 *
 * First-party revenue attribution: top pages + channels by the paid
 * revenue they produced (first-touch join over Payment -> Customer ->
 * Visitor -> first page_view). The moat metric.
 */
export async function getVisitorsAttribution(days = 14) {
  const { data } = await api.get('/v2/visitors/attribution', {
    params: { days },
  });
  return (
    data || {
      windowDays: days,
      totals: {
        revenueCents: 0,
        customers: 0,
        attributedRevenueCents: 0,
        unattributedCustomers: 0,
      },
      topPages: [],
      channels: [],
      fromAI: { customers: 0, revenueCents: 0, engines: [] },
    }
  );
}

// Device split (Mobile / Desktop / Tablet) from the per-day rollup.
export async function getVisitorsDevices(days = 14) {
  const { data } = await api.get('/v2/visitors/devices', { params: { days } });
  return data || { days, total: 0, devices: [] };
}

// When-leads-arrive heatmap: Mon..Sun x [AM, Noon, Eve, Late] form-submit counts.
export async function getVisitorsLeadTimes(days = 14) {
  const { data } = await api.get('/v2/visitors/lead-times', {
    params: { days },
  });
  return data || { days, max: 0, rows: [] };
}

// Per-page insights: views (from rollups) + leads-from-content (form
// submissions keyed by the page they were submitted on), joined on the
// canonical page path. Powers the Content sidebar's page-traffic rail.
export async function getPageInsights(days = 30) {
  const { data } = await api.get('/v2/visitors/page-insights', {
    params: { days },
  });
  return data || { windowDays: days, pages: [] };
}

// Per-form lead counts (windowed + all-time) for the public lead-capture
// forms, with the last submission time.
export async function getFormInsights(days = 30) {
  const { data } = await api.get('/v2/visitors/form-insights', {
    params: { days },
  });
  return data || { windowDays: days, forms: [] };
}

// "What made you money" brief: plain-English synthesis of revenue
// attribution (which page/channel/AI engine produced paid dollars).
export async function getMoneyBrief(days = 30) {
  const { data } = await api.get('/v2/visitors/money-brief', {
    params: { days },
  });
  return data || null;
}

// ROAS: tenant-entered ad spend per channel joined to attributed revenue.
export async function getRoas(days = 30) {
  const { data } = await api.get('/v2/visitors/roas', { params: { days } });
  return data || { windowDays: days, rows: [], totals: {} };
}

// Industry benchmark: this tenant's visit-to-lead conversion rate vs the
// published median for its vertical (ask #2). Returns { enabled: false } when
// the platform switch (BENCHMARKS_ENABLED) is off, so callers render nothing.
// Pass the page's range so the rate matches the "Visit to lead" tile.
export async function getBenchmarks(days = 30) {
  const { data } = await api.get('/v2/visitors/benchmarks', {
    params: { days },
  });
  return data || { enabled: false };
}

// Save monthly ad spend for a channel (businessadmin only, server-enforced).
export async function updateAdSpend(channel, monthlySpendCents) {
  const { data } = await api.put('/v2/visitors/ad-spend', {
    channel,
    monthlySpendCents,
  });
  return data || null;
}

// Per-page on-page SEO health from the latest UAVS scan, keyed by canonical
// path (joins to getPageInsights). Standalone signal; not the UAVS score.
export async function getPageSeo() {
  const { data } = await api.get('/v2/visitors/page-seo');
  return data || { pages: [] };
}

// Live on-site: distinct humans with a page view in the last 5 minutes,
// the busiest current page, and the most recent form start.
export async function getVisitorsLive() {
  const { data } = await api.get('/v2/visitors/live');
  return (
    data || {
      onSiteNow: 0,
      topPath: null,
      topPathReaders: 0,
      lastFormStartAt: null,
      lastFormStartPath: null,
    }
  );
}
